// Géométrie PURE d'un panneau flottant ancré (popover / picker) — DS §6.12.
//
// Le DS §6.9 impose qu'un panneau s'ouvre « sous ce qu'il modifie » : on garde
// donc l'ancrage sur l'affordance, et on corrige seulement ce qui sortait de
// l'écran. Deux corrections, deux retours de QA (17-19 août) :
//
//  1. DÉBORDEMENT À DROITE. `html, body { overflow-x: hidden }` (global.css)
//     clippe net ce qui dépasse : un panneau de 288 px ancré sur une étiquette
//     de locuteur en milieu de phrase se faisait couper. Un simple flip
//     gauche/droite NE SUFFIT PAS — pour une ancre au milieu d'un écran de
//     390 px, aucun des deux bords ne tient. Il faut un décalage continu.
//
//  2. OPTIONS SOUS LE CLAVIER iOS. Un panneau en position absolue ne sait rien
//     de la hauteur du clavier : c'est `window.visualViewport` qui la connaît
//     (il rétrécit quand le clavier monte, dans la WKWebView comme sur le web).
//     On travaille donc sur le viewport VISIBLE, pas sur la fenêtre : bascule
//     au-dessus de l'ancre s'il n'y a plus de place dessous, et hauteur bornée
//     (défilement interne) pour qu'aucune option ne reste hors d'atteinte.
//
// Aucune dépendance DOM ici : l'appelant mesure, ce module décide.

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

// Le viewport VISIBLE (`window.visualViewport`), pas la fenêtre : `height`
// rétrécit quand le clavier monte, `offsetTop` dit de combien la page a été
// poussée sous le haut du viewport de mise en page.
export interface VisibleViewport {
  width: number;
  height: number;
  offsetTop: number;
}

export type AnchoredPanelSide = "below" | "above";

export interface AnchoredPanelPlacement {
  /** Décalage horizontal (px) à appliquer depuis l'alignement `left: 0`. */
  shiftX: number;
  side: AnchoredPanelSide;
  /** Hauteur max (px) tenant dans le viewport visible — défilement au-delà. */
  maxHeight: number;
}

export interface AnchoredPanelInput {
  anchor: AnchorRect;
  panelWidth: number;
  panelHeight: number;
  viewport: VisibleViewport;
  /** Marge minimale gardée avec les bords de l'écran. */
  gutter?: number;
  /** Plancher : sous cette hauteur un panneau n'est plus utilisable. */
  minHeight?: number;
}

const DEFAULT_GUTTER = 8;
const DEFAULT_MIN_HEIGHT = 160;

export function placeAnchoredPanel({
  anchor,
  panelWidth,
  panelHeight,
  viewport,
  gutter = DEFAULT_GUTTER,
  minHeight = DEFAULT_MIN_HEIGHT,
}: AnchoredPanelInput): AnchoredPanelPlacement {
  // Horizontal : on part de l'alignement à gauche de l'ancre, puis on ramène
  // dans [gutter, viewport - gutter - largeur]. Un panneau plus large que
  // l'écran se colle à la gouttière gauche (il sera de toute façon borné en
  // largeur par la CSS `min(…, 100vw − 2×gouttière)`).
  const maxLeft = Math.max(gutter, viewport.width - gutter - panelWidth);
  const left = Math.min(Math.max(anchor.left, gutter), maxLeft);

  // Vertical : tout se mesure contre la fenêtre VISIBLE.
  const visibleTop = viewport.offsetTop;
  const visibleBottom = viewport.offsetTop + viewport.height;
  const spaceBelow = visibleBottom - anchor.bottom - gutter;
  const spaceAbove = anchor.top - visibleTop - gutter;

  // On reste dessous par défaut (DS §6.9 : le panneau tombe sous l'affordance)
  // et on ne bascule que si le dessous ne peut plus rien montrer d'utile.
  const fitsBelow = spaceBelow >= Math.min(panelHeight, minHeight);
  const side: AnchoredPanelSide = fitsBelow || spaceBelow >= spaceAbove ? "below" : "above";
  const space = side === "below" ? spaceBelow : spaceAbove;

  // Plafond dur : un panneau ne peut jamais dépasser ce qui est VISIBLE. Sans
  // ça, une ancre hors écran (page pas encore défilée jusqu'à elle) donne une
  // place « disponible » absurde — 14 360 px mesurés dans Chromium sur /ds.
  const ceiling = viewport.height - 2 * gutter;
  const wanted = Math.max(minHeight, space);

  return {
    shiftX: Math.round(left - anchor.left),
    side,
    maxHeight: Math.round(ceiling > 0 ? Math.min(wanted, ceiling) : wanted),
  };
}
